import { Alert, Box, IconButton } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { useGomakeAxios } from "@/hooks";
import { useRouter } from "next/router";
import { systemAnnouncementsState } from "@/store/system-announcements";
import { getActiveSystemNotificationsApi } from "@/services/api-service/system-announcements/system-announcements-endpoints";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

const DISMISSED_STORAGE_KEY = "gomake-dismissed-system-announcements";

const getDismissedIds = (): number[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "number") : [];
  } catch {
    return [];
  }
};

const saveDismissedIds = (ids: number[]) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
};

const SystemAnnouncementBanner = () => {
  const { primaryColor } = useGomakeTheme();
  const { callApi } = useGomakeAxios();
  const router = useRouter();
  const { pathname } = router;
  const [announcements, setAnnouncements] = useRecoilState(systemAnnouncementsState);
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);

  useEffect(() => {
    setDismissedIds(getDismissedIds());
  }, []);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const callBack = (res: { success?: boolean; data?: typeof announcements }) => {
        if (res.success) {
          setAnnouncements(res.data || []);
        }
      };
      await getActiveSystemNotificationsApi(callApi, callBack);
    };

    fetchAnnouncements().then();
  }, [pathname, callApi, setAnnouncements]);

  const visibleAnnouncements = useMemo(
    () => announcements.filter((item) => !dismissedIds.includes(item.id)),
    [announcements, dismissedIds]
  );

  const handleDismiss = (id: number) => {
    const next = [...dismissedIds, id];
    setDismissedIds(next);
    saveDismissedIds(next);
  };

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1, minWidth: 0 }}>
      {visibleAnnouncements.map((announcement) => (
        <Alert
          key={announcement.id}
          severity="info"
          icon={<CampaignOutlinedIcon sx={{ color: primaryColor(500) }} />}
          action={
            <IconButton
              aria-label="Dismiss announcement"
              color="inherit"
              size="small"
              onClick={() => handleDismiss(announcement.id)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            py: 0.25,
            px: 1,
            alignItems: "center",
            backgroundColor: `${primaryColor(50)}`,
            color: primaryColor(900),
            border: `1px solid ${primaryColor(100)}`,
            "& .MuiAlert-message": {
              ...FONT_FAMILY.Lexend(500, 13),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          {announcement.message}
        </Alert>
      ))}
    </Box>
  );
};

export { SystemAnnouncementBanner };
